<template>
  <div class="notification-popup" @mouseleave="$emit('close')">
    <div class="notification-popup__glow"></div>

    <div class="notification-popup__header">
      <div>
        <p class="notification-popup__eyebrow">Activity Center</p>
        <h3>Notifications</h3>
        <span>Recent account activity and platform updates</span>
      </div>

      <div class="notification-popup__count">
        <strong>{{ notifications.length }}</strong>
        <small>{{ notifications.length === 1 ? 'update' : 'updates' }}</small>
      </div>
    </div>

    <div v-if="notifications.length" class="notification-popup__list">
      <article
        v-for="notification in notifications"
        :key="notification.id"
        class="notification-card"
      >
        <div class="notification-card__accent"></div>

        <div class="notification-card__body">
          <div class="notification-card__meta">
            <span class="notification-card__tag">
              {{ notification.category || 'Notification' }}
            </span>
            <span class="notification-card__time">
              {{ notification.time || 'Just now' }}
            </span>
          </div>

          <h4>{{ notification.title }}</h4>
          <p>{{ notification.body }}</p>
        </div>
      </article>
    </div>

    <div v-else class="notification-popup__empty">
      <div class="notification-popup__empty-icon">
        <i class="far fa-bell-slash"></i>
      </div>
      <h4>All caught up</h4>
      <p>No new notifications are waiting for you right now.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NotificationPopup',
  props: {
    notifications: {
      type: Array,
      default: () => [],
    },
  },
};
</script>

<style scoped>
.notification-popup {
  position: relative;
  width: 380px;
  overflow: hidden;
  border-radius: 28px;
  background: radial-gradient(circle at top right, rgba(55, 162, 82, 0.22), transparent 34%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(244, 249, 245, 0.96));
  border: 1px solid rgba(21, 55, 33, 0.08);
  box-shadow: 0 32px 60px rgba(15, 38, 23, 0.16), 0 12px 28px rgba(15, 38, 23, 0.08);
  backdrop-filter: blur(18px);
}

.notification-popup__glow {
  position: absolute;
  top: -32px;
  right: -22px;
  width: 140px;
  height: 140px;
  border-radius: 999px;
  background: radial-gradient(circle, rgba(67, 179, 95, 0.28), transparent 70%);
  pointer-events: none;
}

.notification-popup__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.2rem 1.25rem 1rem;
  border-bottom: 1px solid rgba(21, 55, 33, 0.08);
}

.notification-popup__eyebrow {
  margin: 0 0 0.35rem;
  color: #2e8f49;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.notification-popup__header h3,
.notification-popup__empty h4,
.notification-card h4,
.notification-card p {
  margin: 0;
}

.notification-popup__header h3 {
  color: #173522;
  font-size: 1.05rem;
  font-weight: 700;
}

.notification-popup__header span {
  display: block;
  margin-top: 0.28rem;
  color: #658072;
  font-size: 0.8rem;
  line-height: 1.45;
}

.notification-popup__count {
  min-width: 74px;
  padding: 0.7rem 0.8rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(46, 143, 73, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
  text-align: center;
}

.notification-popup__count strong {
  display: block;
  color: #184126;
  font-size: 1.05rem;
  line-height: 1;
}

.notification-popup__count small {
  display: block;
  margin-top: 0.28rem;
  color: #6a8376;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.notification-popup__list {
  max-height: 420px;
  padding: 0.7rem;
  overflow-y: auto;
}

.notification-card {
  position: relative;
  display: flex;
  gap: 0.75rem;
  padding: 0.8rem 0.85rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(21, 55, 33, 0.06);
  box-shadow: 0 12px 24px rgba(17, 39, 24, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.notification-card + .notification-card {
  margin-top: 0.65rem;
}

.notification-card:hover {
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 18px 30px rgba(17, 39, 24, 0.09);
}

.notification-card__accent {
  width: 8px;
  flex-shrink: 0;
  border-radius: 999px;
  background: linear-gradient(180deg, #37a252 0%, #1f6d33 100%);
}

.notification-card__body {
  min-width: 0;
}

.notification-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  margin-bottom: 0.4rem;
}

.notification-card__tag {
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.5rem;
  border-radius: 999px;
  background: #eef7f0;
  color: #2b8242;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.notification-card__time {
  color: #72897d;
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
}

.notification-card h4 {
  color: #173522;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.3;
}

.notification-card p {
  margin-top: 0.25rem;
  color: #60786b;
  font-size: 0.75rem;
  line-height: 1.45;
}

.notification-popup__empty {
  padding: 1.8rem 1.4rem 2rem;
  text-align: center;
}

.notification-popup__empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  margin-bottom: 1rem;
  border-radius: 22px;
  background: linear-gradient(135deg, rgba(47, 159, 73, 0.14), rgba(31, 109, 51, 0.08));
  color: #2f8f48;
  font-size: 1.3rem;
}

.notification-popup__empty h4 {
  color: #173522;
  font-size: 1rem;
  font-weight: 700;
}

.notification-popup__empty p {
  margin: 0.45rem 0 0;
  color: #698276;
  font-size: 0.82rem;
  line-height: 1.55;
}

@media (max-width: 767px) {
  .notification-popup {
    width: min(100vw - 32px, 360px);
  }
}
</style>
